import { AdaptivePagination } from "@/components/flexkit/AdaptivePagination";
import { Banner } from "@/components/flexkit/Banner";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useSCIMOperationsLog,
    ResourceType,
    type PlainAuditLogEntry,
} from "@/queries/scim-queries";
import { Timestamp } from "@bufbuild/protobuf";
import { useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, type FC } from "react";

const PAGE_SIZE = 25;

export const SCIMOperationsLog: FC = () => {
    // URL-based page state
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = useMemo(() => {
        const page = searchParams.get("scim_page");
        return page ? parseInt(page, 10) - 1 : 0;
    }, [searchParams]);

    const [searchValue, setSearchValue] = useState("");

    // Reset to page 1 when search changes
    useEffect(() => {
        if (currentPage > 0) {
            const params = new URLSearchParams(searchParams);
            params.delete("scim_page");
            setSearchParams(params);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    // Fetch data
    const { data, isLoading, error, refetch } = useSCIMOperationsLog({});

    const filteredEntries = useMemo(() => {
        if (!data?.entries) return [];
        if (!searchValue.trim()) return data.entries;

        const query = searchValue.toLowerCase();
        return data.entries.filter((entry) => {
            const action = entry.action?.toLowerCase() || "";
            const subjectId = entry.subjectId?.toLowerCase() || "";
            const typeLabel = getResourceTypeLabel(entry.subjectType).toLowerCase();
            return action.includes(query) || subjectId.includes(query) || typeLabel.includes(query);
        });
    }, [data?.entries, searchValue]);

    const totalCount = filteredEntries.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const paginatedEntries = useMemo(() => {
        const startIndex = currentPage * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return filteredEntries.slice(startIndex, endIndex);
    }, [currentPage, filteredEntries]);

    useEffect(() => {
        if (currentPage > 0 && currentPage >= totalPages) {
            const params = new URLSearchParams(searchParams);
            params.delete("scim_page");
            setSearchParams(params);
        }
    }, [currentPage, searchParams, setSearchParams, totalPages]);

    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(searchParams);
        if (page === 0) {
            params.delete("scim_page");
        } else {
            params.set("scim_page", String(page + 1));
        }
        setSearchParams(params);
    };

    return (
        <div className="flex w-full flex-col gap-4" data-track-location={TrackLocations.SCIMOperationsLog}>
            <SearchInput
                wrapperClassName="w-full"
                className="max-w-none"
                placeholder="Search by name or keyword"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onClear={() => setSearchValue("")}
                data-tracking-id="scim-operations-search"
            />

            {/* Error State */}
            {error && (
                <Banner
                    variant="danger"
                    text="Failed to load operations log"
                    action={{
                        text: "Retry",
                        onClick: () => refetch(),
                        "data-tracking-id": "scim-operations-log-retry",
                    }}
                />
            )}

            {/* Loading State */}
            {isLoading && (
                <div className="flex items-center justify-center py-12">
                    <Text className="text-content-secondary">Loading operations...</Text>
                </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && data && filteredEntries.length === 0 && (
                <div className="flex flex-col items-center gap-4 py-12">
                    <Text className="text-content-secondary">No SCIM operations yet</Text>
                    <Text className="max-w-md text-center text-sm text-content-tertiary">
                        Operations will appear here when your identity provider provisions users and groups via SCIM.
                    </Text>
                </div>
            )}

            {/* Operations Table */}
            {!isLoading && !error && data && filteredEntries.length > 0 && (
                <>
                    <Table>
                        <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                            <TableRow className="h-8 text-base text-content-strong">
                                <TableHead className="w-[180px] pl-3">Timestamp</TableHead>
                                <TableHead className="w-[200px] pl-3">Action</TableHead>
                                <TableHead className="w-[160px] pl-3">Type</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody className="border-t border-border-light text-base [&_td:last-child]:text-left">
                            {paginatedEntries.map((entry) => (
                                <OperationsLogRow key={entry.id} entry={entry} />
                            ))}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <AdaptivePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            currentItemsCount={paginatedEntries.length}
                            pageSize={PAGE_SIZE}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const getResourceTypeLabel = (subjectType?: ResourceType) => {
    switch (subjectType) {
        case ResourceType.USER:
            return "User";
        case ResourceType.GROUP:
            return "Group";
        case ResourceType.ACCOUNT:
            return "Account";
        default:
            return "Unknown";
    }
};

// Table row component
const OperationsLogRow: FC<{ entry: PlainAuditLogEntry }> = ({ entry }) => {
    const timestamp = useMemo(() => {
        if (!entry.createdAt) return "N/A";
        const date = new Timestamp(entry.createdAt).toDate();
        return date.toISOString().replace(".000Z", "Z");
    }, [entry.createdAt]);

    const resourceType = useMemo(() => {
        return getResourceTypeLabel(entry.subjectType);
    }, [entry.subjectType]);

    return (
        <TableRow className="h-12 border-border-light">
            <TableCell className="pl-3">
                <Text className="font-mono text-sm text-content-secondary">{timestamp}</Text>
            </TableCell>
            <TableCell className="pl-3">
                <Text className="text-sm text-content-primary">{entry.action || "Unknown"}</Text>
            </TableCell>
            <TableCell className="pl-3">
                <Text className="text-sm text-content-primary">{resourceType}</Text>
            </TableCell>
        </TableRow>
    );
};
